// src/components/resources/tools/generators/FakeChat/WhatsAppPreview.tsx

'use client'

import { cn } from '~/lib/utils'
import { useFakeChatState } from './FakeChatContext'
import type { ChatMessage, ChatParticipant, MessageStatus } from './FakeChat.types'
import { useResolvedMessages } from './useResolvedMessages'
import { IosStatusBar, statusBarTimeFrom } from './IosStatusBar'
import { whatsappNameColor } from './avatarNameColors'

/**
 * WhatsApp delivery ticks. A single grey tick = sent, double grey = delivered,
 * double blue (#53bdeb) = read. `not-delivered` shows a clock glyph. Mirrors
 * the real WhatsApp receipt semantics so the mockup reads as authentic.
 */
function WhatsAppTicks({ status }: { status: MessageStatus }): JSX.Element {
  if (status === 'not-delivered') {
    return (
      <svg viewBox="0 0 16 16" className="h-[15px] w-[15px] text-[#667781]" fill="none" aria-hidden>
        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.2" />
        <path d="M8 5v3l2 1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    )
  }
  const isRead = status === 'read'
  const color = isRead ? '#53bdeb' : '#667781'
  // 'sent' renders a single tick; delivered/read render the double tick.
  const single = status === 'sent'
  return (
    <svg viewBox="0 0 18 14" className="h-[15px] w-[17px]" fill="none" aria-hidden>
      <path
        d="M1.5 7.5 4.2 10.2 9.6 4"
        stroke={color}
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {!single ? (
        <path
          d="M7.4 7.6 9.9 10.2 15.5 4"
          stroke={color}
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : null}
    </svg>
  )
}

interface WhatsAppBubbleProps {
  message: ChatMessage
  participant?: ChatParticipant
  isSelf: boolean
  isContinuation: boolean
  showTimestamps: boolean
  showStatus: boolean
  showReactions: boolean
  isGroup: boolean
}

/**
 * Single WhatsApp bubble. Outgoing (self) bubbles are WhatsApp green
 * (#d9fdd3) and right-aligned with a tail; incoming are white and
 * left-aligned. Group chats show a coloured sender name above the first
 * incoming bubble of each run, matching WhatsApp's group layout.
 */
function WhatsAppBubble({
  message,
  participant,
  isSelf,
  isContinuation,
  showTimestamps,
  showStatus,
  showReactions,
  isGroup
}: WhatsAppBubbleProps): JSX.Element {
  const bubbleColor = isSelf ? '#d9fdd3' : '#ffffff'
  // Sender-name colours for group incoming bubbles — WhatsApp assigns a
  // stable accent per participant; we derive one from the avatar swatch.
  const nameColor = whatsappNameColor(participant?.avatarColor)
  const reactions = showReactions ? message.reactions : []
  const totalReactions = reactions.reduce((sum, r) => sum + r.count, 0)

  return (
    <div className={cn('flex w-full px-[7%]', reactions.length > 0 ? 'pb-[14px]' : 'pb-[3px]', isSelf ? 'justify-end' : 'justify-start')}>
      <div className="relative max-w-[78%]">
        <div
          className={cn(
            'relative rounded-[7.5px] px-[9px] pt-[6px] pb-[8px] text-[14.2px] leading-[19px] shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]',
            isSelf ? 'rounded-tr-[0px]' : 'rounded-tl-[0px]'
          )}
          style={{ background: bubbleColor }}
        >
          {/* Bubble tail */}
          <span
            className={cn('absolute top-0 h-[13px] w-[8px]', isSelf ? 'right-[-8px]' : 'left-[-8px]')}
            aria-hidden
          >
            <svg viewBox="0 0 8 13" className="h-full w-full" fill="none">
              <path
                d={isSelf ? 'M0 0H8C8 0 4 0 0 6V0Z' : 'M8 0H0C0 0 4 0 8 6V0Z'}
                fill={bubbleColor}
              />
            </svg>
          </span>
          {isGroup && !isSelf && !isContinuation && participant?.displayName ? (
            <p className={cn('mb-[1px] text-[12.8px] font-medium leading-[17px]', nameColor)}>
              {participant.displayName}
            </p>
          ) : null}
          {/* WhatsApp tucks the time + ticks into the bottom-right of the
              bubble, with the last line of text wrapping around them. The meta
              is rendered as an inline-block floated to the right *inside* the
              text's own inline flow (not a separate sibling block), so the
              last line of text wraps to its left and it lands at the bubble's
              bottom-right for both single- and multi-line bubbles.

              Vertical alignment is handled structurally, not with a
              `translate-y` nudge (the #135 culprit): the meta's line box is
              given the same `leading-[19px]` as the bubble text, and its
              contents are bottom-aligned with `items-end`, so the small
              timestamp/ticks rest on the text baseline without crowding the
              last line. */}
          <span className="whitespace-pre-wrap break-words text-[#111b21]">
            {/* Strip trailing whitespace/newlines (real WhatsApp does too):
                with `whitespace-pre-wrap`, a pasted message ending in a
                newline would otherwise preserve that blank line right before
                the floated meta and push the timestamp onto an empty line —
                re-opening the #135 misalignment for that input. */}
            {message.text.replace(/\s+$/, '')}
            {showTimestamps || (isSelf && showStatus && message.status) ? (
              <span className="float-right ml-[10px] inline-flex select-none items-end gap-[3px] text-[11px] leading-[19px] text-[#667781]">
                {showTimestamps ? <span>{message.timestamp.trim()}</span> : null}
                {/* Only render ticks when the message carries an explicit
                    status. A `Hidden` (undefined) status renders no receipt —
                    coercing it to 'delivered' here would make the per-message
                    Hidden option a no-op. */}
                {isSelf && showStatus && message.status ? <WhatsAppTicks status={message.status} /> : null}
              </span>
            ) : null}
          </span>
        </div>
        {/* Reaction pill — WhatsApp hangs a rounded white pill off the bubble's
            bottom edge with the emoji(s) and an aggregate count. */}
        {reactions.length > 0 ? (
          <div
            className={cn(
              'absolute bottom-[-12px] flex items-center gap-[2px] rounded-full border border-[#e9edef] bg-white px-[6px] py-[1px] text-[12px] shadow-[0_1px_1px_rgba(11,20,26,0.13)]',
              isSelf ? 'right-[8px]' : 'left-[8px]'
            )}
          >
            {reactions.slice(0, 3).map(r => (
              <span key={r.id}>{r.emoji}</span>
            ))}
            {totalReactions > 1 ? <span className="ml-[1px] text-[11px] text-[#667781]">{totalReactions}</span> : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}

/**
 * WhatsApp platform preview — phone-framed, with the signature teal header,
 * doodle-tinted wallpaper, green/white bubbles, and a rounded composer.
 */
export function WhatsAppPreview(): JSX.Element {
  const { conversation } = useFakeChatState()
  const resolvedMessages = useResolvedMessages(conversation.messages)
  const isGroup = conversation.metadata.channelType !== 'dm'
  const contactName = conversation.metadata.channelName || 'Contact'
  const presence = conversation.metadata.lastActivityLabel || 'online'
  const contactInitial = contactName.charAt(0)?.toUpperCase() ?? 'A'
  const statusBarTime = statusBarTimeFrom(resolvedMessages[0]?.message.timestamp)

  return (
    <div className="relative flex w-full justify-center p-6">
      <div className="relative h-[760px] w-[360px] rounded-[54px] border border-black/40 bg-black/95 p-4 shadow-[0_30px_70px_rgba(15,23,42,0.35)]">
        <div
          className="relative flex h-full flex-col overflow-hidden rounded-[44px]"
          style={{ fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, system-ui, sans-serif" }}
        >
          {/* Notch */}
          <div className="absolute left-1/2 top-3 z-20 h-7 w-32 -translate-x-1/2 rounded-full bg-black" aria-hidden />

          {/* Status bar + header (teal) — status bar clears the notch so the
              header title is never tucked behind the Dynamic Island. */}
          <div className="relative z-10 bg-[#008069] text-white">
            <IosStatusBar time={statusBarTime} tone="light" />
            <header className="flex items-center gap-3 px-3 pb-[10px] pt-[5px]">
            <svg viewBox="0 0 24 24" className="h-6 w-5 shrink-0" fill="none" aria-hidden>
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/25 text-base font-semibold">
              {contactInitial}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[16px] font-semibold leading-tight">{contactName}</p>
              <p className="truncate text-[12.5px] leading-tight text-white/85">{presence}</p>
            </div>
            <div className="flex items-center gap-4 pr-1">
              <svg viewBox="0 0 24 24" className="h-[22px] w-[22px]" fill="none" aria-hidden>
                <path d="M23 7l-7 5 7 5V7z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                <rect x="1" y="5" width="15" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
              </svg>
              <svg viewBox="0 0 24 24" className="h-[20px] w-[20px]" fill="currentColor" aria-hidden>
                <path d="M20 15.5a12.5 12.5 0 0 1-3.9-.6 1.1 1.1 0 0 0-1.1.3l-1.7 1.7a13.3 13.3 0 0 1-5.9-5.9l1.7-1.7a1.1 1.1 0 0 0 .3-1.1A12.5 12.5 0 0 1 8.5 4 1.1 1.1 0 0 0 7.5 3H4.4A1.1 1.1 0 0 0 3.3 4 17.7 17.7 0 0 0 20 20.7a1.1 1.1 0 0 0 1-1.1V16.5a1.1 1.1 0 0 0-1-1z" />
              </svg>
              <svg viewBox="0 0 24 24" className="h-[20px] w-[20px]" fill="currentColor" aria-hidden>
                <circle cx="12" cy="5" r="1.6" />
                <circle cx="12" cy="12" r="1.6" />
                <circle cx="12" cy="19" r="1.6" />
              </svg>
            </div>
            </header>
          </div>

          {/* Wallpaper + messages */}
          <div
            className="no-scrollbar relative flex flex-1 flex-col overflow-auto py-3"
            style={{ background: '#efeae2' }}
          >
            {/* Subtle WhatsApp doodle tint */}
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 20% 30%, #0b141a 1px, transparent 1px), radial-gradient(circle at 70% 60%, #0b141a 1px, transparent 1px)',
                backgroundSize: '34px 34px'
              }}
              aria-hidden
            />
            {/* Encryption + date chips */}
            <div className="relative z-10 flex flex-col items-center gap-2 pb-2">
              <span className="rounded-[8px] bg-[#ffe6cb] px-3 py-[6px] text-center text-[11.5px] leading-snug text-[#54656f] shadow-sm">
                🔒 Messages are end-to-end encrypted.
              </span>
              <span className="rounded-[7px] bg-[#ffffff] px-[10px] py-[4px] text-[11.5px] font-medium uppercase text-[#54656f] shadow-sm">
                Today
              </span>
            </div>
            <div className="relative z-10 flex flex-col">
              {resolvedMessages.map(({ message, participant, isSelf, isContinuation }) => (
                <WhatsAppBubble
                  key={message.id}
                  message={message}
                  participant={participant}
                  isSelf={isSelf}
                  isContinuation={isContinuation}
                  showTimestamps={conversation.metadata.showTimestamps}
                  showStatus
                  showReactions={conversation.metadata.showReactions}
                  isGroup={isGroup}
                />
              ))}
              {conversation.metadata.typingIndicator ? (
                <div className="flex justify-start px-[7%] pt-1">
                  <div className="flex items-center gap-[4px] rounded-[7.5px] rounded-tl-[0px] bg-white px-3 py-[10px] shadow-sm">
                    <span className="h-[7px] w-[7px] animate-pulse rounded-full bg-[#9aa6ac]" />
                    <span className="h-[7px] w-[7px] animate-pulse rounded-full bg-[#9aa6ac]" style={{ animationDelay: '0.15s' }} />
                    <span className="h-[7px] w-[7px] animate-pulse rounded-full bg-[#9aa6ac]" style={{ animationDelay: '0.3s' }} />
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* Composer */}
          <div className="flex items-end gap-2 bg-[#f0f2f5] px-2 py-[8px]">
            <div className="flex flex-1 items-center gap-2 rounded-[24px] bg-white px-3 py-[9px] shadow-sm">
              <svg viewBox="0 0 24 24" className="h-[22px] w-[22px] text-[#54656f]" fill="none" aria-hidden>
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                <path d="M8.5 14s1.3 1.6 3.5 1.6 3.5-1.6 3.5-1.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <circle cx="9" cy="10" r="1" fill="currentColor" />
                <circle cx="15" cy="10" r="1" fill="currentColor" />
              </svg>
              <span className="flex-1 text-[15px] text-[#8696a0]">Message</span>
              <svg viewBox="0 0 24 24" className="h-[21px] w-[21px] text-[#54656f]" fill="none" aria-hidden>
                <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <svg viewBox="0 0 24 24" className="h-[21px] w-[21px] text-[#54656f]" fill="none" aria-hidden>
                <rect x="3" y="6" width="18" height="13" rx="3" stroke="currentColor" strokeWidth="1.8" />
                <circle cx="12" cy="12.5" r="3" stroke="currentColor" strokeWidth="1.8" />
                <path d="M8 6l1.5-2h5L16 6" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              </svg>
            </div>
            <button
              type="button"
              className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full bg-[#008069] text-white"
              aria-label="Voice message"
            >
              <svg viewBox="0 0 24 24" className="h-[22px] w-[22px]" fill="currentColor" aria-hidden>
                <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3z" />
                <path d="M6 11a6 6 0 0 0 12 0M12 17v3" stroke="white" strokeWidth="1.6" fill="none" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
